import { useEffect, useState } from "preact/hooks";
import { APP_NAME, html } from "./lib.js";
import { getLang, LANGS, setLang, tr } from "./i18n.js";

let logoSeq = 0;
/** RELAYZA wordmark: the relay arrow from relayza.company plus the name with a teal dot. */
export function Logo({ big = false }) {
  const [id] = useState(() => `rz-grad-${++logoSeq}`);
  return html`<div class="brand ${big ? "big" : ""}">
    <svg viewBox="0 0 58 48" aria-hidden="true">
      <defs><linearGradient id=${id} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#08f1c8" /><stop offset=".55" stop-color="#05bde7" /><stop offset="1" stop-color="#316bff" /></linearGradient></defs>
      <path d="M3 5h20c5 0 7 2 11 6l19 13-19 13c-4 4-6 6-11 6H3v-9h18l12-10-12-10H3z" fill=${`url(#${id})`} />
      <path d="M3 19h19l11 5-11 5H3z" fill="#167dff" />
    </svg>
    <span>${APP_NAME}<span class="brand-dot">.</span></span>
  </div>`;
}

/** Switches between English and Hebrew; labelled with the other language's own name. */
export function LangToggle({ class: cls = "link" }) {
  const other = LANGS.find((l) => l.code !== getLang()) ?? LANGS[0];
  return html`<button type="button" class=${cls} lang=${other.code} title=${tr("Change language")} onClick=${() => setLang(other.code)}>${other.label}</button>`;
}

let toastSetter = null;
export function toast(message, kind = "ok") {
  toastSetter?.({ message, kind, id: Math.random() });
}
export function Toaster() {
  const [t, setT] = useState(null);
  toastSetter = setT;
  useEffect(() => {
    if (!t) return;
    const id = setTimeout(() => setT(null), 3500);
    return () => clearTimeout(id);
  }, [t]);
  return t ? html`<div class="toast ${t.kind}" role="status">${t.message}</div>` : null;
}

/** Loads data with `fn` and re-runs when `deps` change. Returns [data, reload, error]. */
export function useLoad(fn, deps = []) {
  const [state, setState] = useState({ data: null, error: null });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    fn()
      .then((data) => alive && setState({ data, error: null }))
      .catch((error) => alive && setState({ data: null, error }));
    return () => {
      alive = false;
    };
  }, [...deps, tick]);
  return [state.data, () => setTick((n) => n + 1), state.error];
}

export function usePoll(reload, ms) {
  useEffect(() => {
    const id = setInterval(() => document.visibilityState === "visible" && reload(), ms);
    return () => clearInterval(id);
  }, [reload, ms]);
}

/** Wraps an async action with busy state + error toast. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      const r = await fn();
      if (okMessage) toast(okMessage);
      return r;
    } catch (err) {
      toast(err.message ?? String(err), "error");
      throw err;
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

export const Loading = () => html`<div class="loading" aria-busy="true">${tr("Loading…")}</div>`;
export const ErrorBox = ({ error }) => html`<div class="callout error">${error?.message ?? tr("Something went wrong")}</div>`;

export function PageHeader({ title, subtitle, children }) {
  return html`<header class="page-header">
    <div><h1>${title}</h1>${subtitle && html`<p class="muted">${subtitle}</p>`}</div>
    <div class="actions">${children}</div>
  </header>`;
}

export function Field({ label, hint, children }) {
  return html`<label class="field"><span class="label">${label}</span>${children}${hint && html`<span class="hint">${hint}</span>`}</label>`;
}

export function Badge({ kind = "neutral", children }) {
  return html`<span class="badge ${kind}">${children}</span>`;
}

export function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return html`<div class="modal-backdrop" onClick=${(e) => e.target === e.currentTarget && onClose()}>
    <div class="modal ${wide ? "wide" : ""}" role="dialog" aria-modal="true" aria-label=${title}>
      <div class="modal-head"><h2>${title}</h2><button class="icon-btn" aria-label=${tr("Close")} onClick=${onClose}>×</button></div>
      <div class="modal-body">${children}</div>
    </div>
  </div>`;
}

export function Empty({ title, children }) {
  return html`<div class="empty"><strong>${title}</strong><div class="muted">${children}</div></div>`;
}

/** Simple controlled form state helper. */
export function useForm(initial) {
  const [values, setValues] = useState(initial);
  const bind = (key, transform = (v) => v) => ({
    value: values[key] ?? "",
    onInput: (e) => setValues((v) => ({ ...v, [key]: transform(e.target.value) })),
  });
  const check = (key) => ({
    checked: !!values[key],
    onChange: (e) => setValues((v) => ({ ...v, [key]: e.target.checked })),
  });
  return { values, setValues, bind, check };
}
